/*
 * Copyright (c) 2026, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

// A lazily-loaded route chunk 404s when the browser still has an old tab
// open after a new build has been deployed (the hashed filename it's
// requesting no longer exists). Vite/browsers throw a plain TypeError for
// this - there's no dedicated error class - and the message wording differs
// per engine, e.g.:
//   Chrome:  "Failed to fetch dynamically imported module: <url>"
//   Firefox: "error loading dynamically imported module: <url>"
//   Safari:  "Importing a module script failed."
// `ChunkLoadError` is kept for compatibility with the webpack/CRA-based
// error this code used to run under, in case anything still throws it.
const DYNAMIC_IMPORT_ERROR_PATTERN =
  /dynamically imported module|importing a module script failed|loading chunk .* failed/i;

export function isDynamicImportError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }
  return (
    error.name === 'ChunkLoadError' ||
    DYNAMIC_IMPORT_ERROR_PATTERN.test(error.message)
  );
}

// Guards against reloading forever if the error keeps recurring after a
// reload (e.g. the deploy itself is broken, not just a stale tab) - only
// ever attempt the automatic reload once per session. Call
// clearReloadOnceGuard() once the app has rendered successfully so a
// future, unrelated stale-chunk incident can also reload exactly once.
const RELOADED_FLAG_KEY = 'choreo-console:reloaded-after-import-error';

export function reloadOnceForDynamicImportError(error: unknown): boolean {
  if (
    !isDynamicImportError(error) ||
    sessionStorage.getItem(RELOADED_FLAG_KEY) === 'true'
  ) {
    return false;
  }
  sessionStorage.setItem(RELOADED_FLAG_KEY, 'true');
  window.location.reload();
  return true;
}

export function clearReloadOnceGuard(): void {
  sessionStorage.removeItem(RELOADED_FLAG_KEY);
}
